import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { apiRequest } from "../api/apiClient";
import { ARAGON_COLORS, useAragonViewport } from "../theme/AragonTheme";

const LOGO_SRC = "/images/1080 x 1080 px Logo 4.png";

const legalContent = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro:
      "This page establishes the presentation structure for Aragon's Privacy Policy. Production legal language should be reviewed and approved by counsel before release.",
    sections: [
      ["01", "Information handled", "Describe the account, business, transaction and technical information Aragon receives through the product."],
      ["02", "Purpose and use", "Explain how information supports authentication, valuation workflows, buyer discovery, transaction operations and service improvement."],
      ["03", "Sharing and access", "Define which service providers, authorized participants and legal obligations can result in information being shared."],
      ["04", "Retention and security", "Document retention periods, security safeguards and the controls available to authorized users."],
      ["05", "Rights and contact", "State the applicable privacy rights, request process and contact route for privacy questions."],
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    intro:
      "This page establishes the presentation structure for Aragon's Terms of Service. Production contractual language should be reviewed and approved by counsel before release.",
    sections: [
      ["01", "Service scope", "Define the Aragon services, permitted use and the distinction between product information and professional advice."],
      ["02", "Accounts and authority", "Set expectations for account security, organizational authority, role permissions and accurate information."],
      ["03", "Transaction information", "Define responsibilities around uploaded documents, business data, confidentiality and counterparty access."],
      ["04", "Platform conduct", "Describe prohibited use, misuse of the service and actions that can result in suspension or termination."],
      ["05", "Commercial and legal terms", "Document fees, disclaimers, liability allocation, governing law and dispute provisions after legal review."],
    ],
  },
};

function PublicHeader({ viewport }) {
  return (
    <Box
      component="header"
      sx={{
        minHeight: 74,
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.14)}`,
        bgcolor: ARAGON_COLORS.ivory,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: `${viewport.contentWidth}px`,
          mx: "auto",
          px: `${viewport.gutter}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
        }}
      >
        <RouterLink to="/">
          <Box component="img" src={LOGO_SRC} alt="Aragon" sx={{ width: 44, height: 44, objectFit: "contain" }} />
        </RouterLink>
        <Stack direction="row" spacing={{ xs: 1.8, sm: 3 }}>
          {[
            ["/contact", "Contact"],
            ["/privacy", "Privacy"],
            ["/terms", "Terms"],
          ].map(([to, label]) => (
            <MuiLink
              key={to}
              component={RouterLink}
              to={to}
              underline="none"
              sx={{ color: ARAGON_COLORS.navy, fontSize: 12.5, fontWeight: 700 }}
            >
              {label}
            </MuiLink>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function ContactContent({ viewport }) {
  const [form, setForm] = useState({
    reason: "sell",
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ tone: "", text: "" });
  const submitLockRef = useRef(false);
  const update = (name) => (event) => {
    setForm((current) => ({ ...current, [name]: event.target.value }));
    setStatus({ tone: "", text: "" });
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;
    setBusy(true);
    setStatus({ tone: "", text: "" });
    try {
      await apiRequest("/api/contact", {
        method: "POST",
        body: {
          reason: form.reason,
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          message: form.message.trim(),
        },
      });
      setStatus({ tone: "success", text: "Your enquiry has been sent to the appropriate Aragon team." });
      setForm((current) => ({ ...current, message: "" }));
    } catch (error) {
      setStatus({ tone: "error", text: error.message || "The enquiry could not be sent." });
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: viewport.tablet ? "1fr" : "0.78fr 1.22fr",
        gap: { xs: 5, md: 9 },
        alignItems: "start",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
          Contact
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1.4,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: `${viewport.legalTitle}px`,
            lineHeight: 0.98,
            letterSpacing: "-0.035em",
            color: ARAGON_COLORS.navy,
          }}
        >
          Talk to the right team.
        </Typography>
        <Typography sx={{ mt: 2.2, maxWidth: 440, color: ARAGON_COLORS.steel, lineHeight: 1.7, fontSize: 14 }}>
          Tell us what you are trying to accomplish. Aragon can route the enquiry to the appropriate business, buyer or partnership workflow.
        </Typography>
        <Stack sx={{ mt: 4 }}>
          {[
            ["Sell-side", "Business owners considering valuation or a sale process."],
            ["Buy-side", "Buyers evaluating acquisition opportunities."],
            ["Partnership", "Professional partners supporting transaction work."],
            ["General", "Product, company or other enquiries."],
          ].map(([title, body]) => (
            <Box key={title} sx={{ py: 1.8, borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.14)}` }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: ARAGON_COLORS.navy }}>{title}</Typography>
              <Typography sx={{ mt: 0.45, color: ARAGON_COLORS.steel, fontSize: 12.5, lineHeight: 1.55 }}>{body}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.16)}`,
          p: { xs: 2.5, sm: 3.5, md: 4.5 },
          bgcolor: alpha(ARAGON_COLORS.ivory, 0.72),
        }}
      >
        <Stack spacing={1.8}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: ARAGON_COLORS.steel }}>I want to</InputLabel>
            <Select value={form.reason} label="I want to" onChange={update("reason")} sx={{ color: ARAGON_COLORS.navy }}>
              <MenuItem value="sell">Sell or value a business</MenuItem>
              <MenuItem value="buy">Buy a business</MenuItem>
              <MenuItem value="partner">Discuss a partnership</MenuItem>
              <MenuItem value="general">Ask a general question</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Name" value={form.name} onChange={update("name")} required fullWidth />
          <TextField label="Email" type="email" value={form.email} onChange={update("email")} required fullWidth />
          <TextField label="Company" value={form.company} onChange={update("company")} required fullWidth />
          <TextField label="Message" value={form.message} onChange={update("message")} required fullWidth multiline minRows={5} />
          {status.text && (
            <Box
              role={status.tone === "error" ? "alert" : "status"}
              sx={{
                px: 1.5,
                py: 1.2,
                border: `1px solid ${alpha(status.tone === "error" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel, 0.48)}`,
                bgcolor: alpha(status.tone === "error" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel, 0.08),
                color: status.tone === "error" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy,
                fontSize: 12.5,
              }}
            >
              {status.text}
            </Box>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={busy}
            sx={{
              alignSelf: { xs: "stretch", sm: "flex-end" },
              minWidth: 150,
              minHeight: 42,
              bgcolor: ARAGON_COLORS.navy,
              color: ARAGON_COLORS.ivory,
              "&:hover": { bgcolor: ARAGON_COLORS.wine },
            }}
          >
            {busy ? "Sending…" : "Send enquiry"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function LegalContent({ type, viewport }) {
  const content = legalContent[type];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: viewport.tablet ? "1fr" : "0.42fr 1fr",
        gap: { xs: 5, md: 10 },
        alignItems: "start",
      }}
    >
      <Box sx={{ position: viewport.tablet ? "static" : "sticky", top: 32 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
          {content.eyebrow}
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1.4,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: `${viewport.legalTitle}px`,
            lineHeight: 0.98,
            letterSpacing: "-0.035em",
            color: ARAGON_COLORS.navy,
          }}
        >
          {content.title}
        </Typography>
        <Typography sx={{ mt: 2.2, color: ARAGON_COLORS.steel, fontSize: 13.5, lineHeight: 1.7 }}>
          Last updated: 27 August 2026
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ maxWidth: 760, color: ARAGON_COLORS.steel, fontSize: 14.5, lineHeight: 1.8 }}>
          {content.intro}
        </Typography>
        <Stack sx={{ mt: 4 }}>
          {content.sections.map(([number, title, body]) => (
            <Box
              key={number}
              sx={{
                display: "grid",
                gridTemplateColumns: "46px 1fr",
                gap: 2,
                py: 3,
                borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.16)}`,
              }}
            >
              <Typography sx={{ fontSize: 11, color: ARAGON_COLORS.steel }}>{number}</Typography>
              <Box>
                <Typography sx={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 23, color: ARAGON_COLORS.navy }}>
                  {title}
                </Typography>
                <Typography sx={{ mt: 1.1, maxWidth: 720, color: ARAGON_COLORS.steel, fontSize: 13.5, lineHeight: 1.75 }}>
                  {body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function PublicInfo() {
  const viewport = useAragonViewport();
  const location = useLocation();
  const type = useMemo(() => {
    if (location.pathname === "/privacy") {
      return "privacy";
    }
    if (location.pathname === "/terms") {
      return "terms";
    }
    return "contact";
  }, [location.pathname]);
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: ARAGON_COLORS.ivory, color: ARAGON_COLORS.navy }}>
      <PublicHeader viewport={viewport} />
      <Box
        component="main"
        sx={{
          width: "100%",
          maxWidth: `${viewport.contentWidth}px`,
          mx: "auto",
          px: `${viewport.gutter}px`,
          py: `${viewport.pageY}px`,
        }}
      >
        {type === "contact" ? <ContactContent viewport={viewport} /> : <LegalContent type={type} viewport={viewport} />}
      </Box>
    </Box>
  );
}

export default PublicInfo;
